import React, { useEffect, useState } from "react"
import styled from "styled-components"
import InputDatePicker from "@src/components/reusable/forms/input-datepicker-v2"
import { useFormContext } from "react-hook-form"
import { isWithinInterval } from "date-fns"

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.gray200};
  padding-bottom: 40px;
  padding-top: 40px;

  @media (max-width: 660px) {
    flex-direction: column;
    gap: 24px;
  }
`

const Title = styled.div`
  color: ${props => props.theme.gray900};
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  @media (max-width: 500px) {
    font-size: 18px;
    line-height: 24px;
  }
`

const Row = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 32px;
  .input-datepicker {
    width: 100%;
    & .end_date {
      cursor: not-allowed;
      background: rgba(30, 30, 30, 0.15);
    }
  }
  @media (max-width: 660px) {
    align-items: flex-end;

    flex-direction: column;
    gap: 24px;
  }
`

const Column = styled.div`
  width: 69%;
  gap: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  @media (max-width: 660px) {
    width: 100%;
    gap: 24px;
  }
`

const Result = styled.div`
  border-radius: 8px;
  background: ${props => props.theme.secondary50};
  color: ${props => props.theme.secondary600};
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  display: flex;
  justify-content: space-between;
  padding: 16px;
  text-wrap: nowrap;
  gap: 8px;
  width: max-content;
`

const OptionsStartFinish = ({ nights, pricingTable }) => {
  const { watch, setValue } = useFormContext()
  const startDate = watch("dates.start")
  const [endDate, setEndDate] = useState("")
  useEffect(() => {
    if (!startDate || !nights) return

    const start = new Date(startDate)
    const end = new Date(start)
    end.setDate(start.getDate() + nights)

    const formatted = end.toISOString().split("T")[0]
    setEndDate(formatted)
    // For sharing across components
    setValue("dates.calculated_end", end.toISOString())
    // Optional: store in form state if needed
    setValue("dates.end", formatted)
  }, [startDate, nights, setValue])
  const filterTravelDate = date => {
    return date
  }

  const [validDateRanges, setValidDateRanges] = useState([])

  useEffect(() => {
    const ranges = []

    pricingTable?.[0]?.rows?.forEach(row => {
      const name = row.name || ""
      const match = name.match(
        /([A-Za-z]+ \d{1,2}, \d{4}) to ([A-Za-z]+ \d{1,2}, \d{4})/,
      )
      if (match) {
        const start = new Date(match[1])
        const end = new Date(match[2])
        ranges.push({ start, end })
      }
    })

    setValidDateRanges(ranges)
  }, [pricingTable])

  const isDateValid = date => {
    return validDateRanges.some(range =>
      isWithinInterval(date, { start: range.start, end: range.end }),
    )
  }

  return (
    <Wrapper>
      <Title>Trip Dates</Title>
      <Column>
        <Row>
          <InputDatePicker
            name="dates.start"
            label="Start date"
            icon="calendar-dates"
            placeholder={"Pick a date"}
            displayFormat="MMMM d, yyyy"
            filterDate={isDateValid}
            required
          />

          <InputDatePicker
            className="end_date"
            name="dates.end"
            label="End date"
            value={endDate}
            displayFormat="MMMM d, yyyy"
            filterDate={filterTravelDate}
            required={false}
            disabled
            dropdown={false}
          />
        </Row>
        <Row>
          <Result>
            <HalfMoon /> {nights} nights
          </Result>
        </Row>
      </Column>
    </Wrapper>
  )
}

export default OptionsStartFinish

const HalfMoon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <g clipPath="url(#clip0_8121_287)">
        <path
          d="M12.5001 19.9998C11.0997 19.9987 9.71508 19.7041 8.43559 19.1348C7.1561 18.5655 6.01018 17.7343 5.07179 16.6948C4.12632 15.6541 3.41297 14.4242 2.97908 13.0867C2.54519 11.7493 2.40068 10.3348 2.55513 8.93729C2.80413 6.7623 3.75296 4.72706 5.25876 3.138C6.76457 1.54893 8.74584 0.492042 10.9043 0.126454C12.2996 -0.0890668 13.7236 -0.0310443 15.0968 0.297288C15.4564 0.38967 15.7847 0.576458 16.0479 0.838322C16.311 1.10019 16.4994 1.42761 16.5936 1.78672C16.6877 2.14583 16.6842 2.52358 16.5834 2.88088C16.4826 3.23817 16.2881 3.56203 16.0201 3.81895C12.2201 7.29062 12.5501 12.689 16.6926 15.8273C16.9855 16.0572 17.2115 16.3614 17.3469 16.7082C17.4824 17.055 17.5224 17.4318 17.4629 17.7994C17.4033 18.1669 17.2463 18.5118 17.0083 18.7981C16.7702 19.0844 16.4598 19.3017 16.1093 19.4273C14.9441 19.808 13.7259 20.0012 12.5001 19.9998Z"
          fill="#315254"
        />
      </g>
      <defs>
        <clipPath id="clip0_8121_287">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}
